import React, { useState, useEffect } from 'react';
import { Crown, Diamond, Star, Award, Users, DollarSign, TrendingUp, Calendar, Search, Download, Plus, Phone, Mail, MapPin, User, Heart, Globe, Eye, X, BarChart3 } from 'lucide-react';

const VipManagement = () => {
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedOrigin, setSelectedOrigin] = useState('all');
  const [selectedActivity, setSelectedActivity] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);

  // Tiers VIP
  const vipTiers = [
    {
      id: 'gold',
      name: 'Gold',
      icon: '🥇',
      count: 142,
      range: '€50K - €200K/an',
      color: 'yellow'
    },
    {
      id: 'platinum',
      name: 'Platinum',
      icon: '💎',
      count: 87,
      range: '€200K - €500K/an',
      color: 'gray'
    },
    {
      id: 'diamond',
      name: 'Diamond',
      icon: '💠',
      count: 63,
      range: '€500K - €1M/an',
      color: 'blue'
    },
    {
      id: 'black',
      name: 'Black',
      icon: '👑',
      count: 32,
      range: '€1M+/an',
      color: 'black'
    }
  ];

  // Clients VIP
  const vipClients = [
    {
      id: 'james-davidson',
      initials: 'JD',
      name: 'James Davidson',
      title: 'CEO, Davidson Holdings',
      location: 'New York',
      tier: 'black',
      lifetimeValue: 2400000,
      sejours: 18,
      score: 4.9,
      preferences: ['⚠️ Allergie Arachides', 'St-Barth', 'Villa 6+ chambres', 'Chef Michelin', 'Yacht 70m+'],
      recentActivity: [
        { date: '15 Dec', type: 'Réservation', description: 'Villa Azure Dreams', amount: 280000 },
        { date: '8 Nov', type: 'Charter', description: 'M/Y Serenity', amount: 850000 }
      ],
      since: '2019',
      vipSince: '2020',
      bookings: 18,
      topClient: true
    },
    {
      id: 'sophie-chen',
      initials: 'SC',
      name: 'Sophie Chen',
      title: 'Fashion Designer',
      location: 'Paris',
      tier: 'diamond',
      lifetimeValue: 875000,
      sejours: 12,
      score: 5.0,
      preferences: ['🌱 Végétarienne', 'Mykonos', 'Design moderne', 'Yoga matinal', 'Art galleries'],
      recentActivity: [
        { date: '20 Sep', type: 'Charter', description: 'Yacht Serenity', amount: 175000 },
        { date: '15 Jul', type: 'Villa', description: 'Villa Sunset - Santorini', amount: 120000 }
      ],
      since: '2021',
      vipSince: '2021',
      bookings: 12
    },
    {
      id: 'mohammed-al-rashid',
      initials: 'MA',
      name: 'Mohammed Al-Rashid',
      title: 'Real Estate Investor',
      location: 'Dubai',
      tier: 'diamond',
      lifetimeValue: 650000,
      sejours: 8,
      score: 4.8,
      preferences: ['👨‍👩‍👧‍👦 Famille (6)', 'St-Tropez', 'Plage privée', 'Halal', 'Sécurité 24/7'],
      recentActivity: [
        { date: '12 Sep', type: 'Villa', description: 'Villa Paradiso', amount: 450000 }
      ],
      since: '2020',
      vipSince: '2021',
      bookings: 8
    },
    {
      id: 'emma-williams',
      initials: 'EW',
      name: 'Emma Williams',
      title: 'Tech Entrepreneur',
      location: 'London',
      tier: 'gold',
      lifetimeValue: 185000,
      sejours: 3,
      score: 4.7,
      preferences: ['Sardaigne', 'Couple', 'Spa', 'Wine tasting'],
      recentActivity: [
        { date: '25 Sep', type: 'Villa', description: 'Villa Sunset', amount: 95000 }
      ],
      since: '2024',
      vipSince: '2024',
      bookings: 3
    }
  ];

  // Détails client exemple (James Davidson)
  const clientDetails = {
    contact: {
      phone: '+1 212 555 0100',
      email: 'j.davidson@davidsonholdings.com',
      assistant: 'Sarah Miller',
      address: 'Manhattan, NY'
    },
    family: [
      { initials: 'MD', name: 'Margaret Davidson', role: 'Épouse' },
      { initials: 'TD', name: 'Thomas Davidson', role: 'Fils (16 ans)' },
      { initials: 'SD', name: 'Sophie Davidson', role: 'Fille (14 ans)' }
    ],
    financial: {
      totalLifetime: 2400000,
      thisYear: 1130000,
      averageBooking: 133000,
      totalCommission: 480000,
      totalStays: 18,
      averageRating: 4.9,
      satisfactionRate: 94,
      bookings2025: 3
    },
    preferences: {
      destinations: 'St-Barth (70%), St-Tropez (20%), Mykonos (10%)',
      cuisine: 'Française, Japonaise, Méditerranéenne',
      allergies: 'Arachides (famille entière)',
      wines: 'Bordeaux grands crus, Champagne millésimé',
      bedding: 'Oreillers fermes, draps coton égyptien',
      activities: 'Golf, Tennis, Plongée, Yacht day trips'
    },
    history: [
      {
        property: 'Villa Azure Dreams - St-Barth',
        dates: '15-29 Décembre 2025',
        duration: '14 nuits',
        guests: '12 invités',
        amount: 280000,
        status: 'Confirmé'
      },
      {
        property: 'M/Y Serenity - Méditerranée',
        dates: '8-15 Novembre 2025',
        duration: '7 nuits',
        guests: '10 invités',
        amount: 850000,
        status: 'Confirmé'
      },
      {
        property: 'Villa Paradiso - St-Tropez',
        dates: '1-15 Août 2025',
        duration: '14 nuits',
        guests: '12 invités',
        amount: 450000,
        status: 'Complété'
      }
    ],
    notes: [
      {
        author: 'Sarah (Concierge)',
        date: '5 Dec 2025',
        content: 'M. Davidson préfère être contacté par son assistant entre 9h-17h EST. Très attentif aux détails de sécurité pour sa famille. Apprécie les surprises pour l\'anniversaire de son épouse (15 mars).',
        tags: ['Important', 'Famille']
      },
      {
        author: 'Pierre (Chef)',
        date: '20 Aug 2025',
        content: 'Famille très agréable. Les enfants adorent les desserts au chocolat. Mme Davidson est passionnée de cuisine japonaise authentique. Prévoir sushis chef pour au moins 2 soirées.',
        tags: ['Cuisine', 'Préférences']
      }
    ]
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'black': return 'bg-gray-900 text-white';
      case 'diamond': return 'bg-blue-600 text-white';
      case 'platinum': return 'bg-gray-600 text-white';
      case 'gold': return 'bg-yellow-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'black': return '👑';
      case 'diamond': return '💠';
      case 'platinum': return '💎';
      case 'gold': return '🥇';
      default: return '👤';
    }
  };

  const VipClientCard = ({ client }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg ${getTierColor(client.tier)}`}>
            {getTierIcon(client.tier)}
          </div>
          <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
            {client.initials}
          </div>
        </div>
        {client.topClient && (
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
            Top 1%
          </span>
        )}
      </div>

      <h3 className="font-semibold text-gray-800 text-lg mb-1">{client.name}</h3>
      <p className="text-sm text-gray-600 mb-2">{client.title} • {client.location}</p>

      <div className="grid grid-cols-3 gap-4 mb-4 text-center">
        <div>
          <div className="font-bold text-green-600">€{(client.lifetimeValue / 1000).toFixed(0)}K</div>
          <div className="text-xs text-gray-600">Lifetime</div>
        </div>
        <div>
          <div className="font-bold text-gray-800">{client.sejours}</div>
          <div className="text-xs text-gray-600">Séjours</div>
        </div>
        <div>
          <div className="font-bold text-gray-800">{client.score}</div>
          <div className="text-xs text-gray-600">Score</div>
        </div>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Préférences Clés</h4>
        <div className="flex flex-wrap gap-1">
          {client.preferences.slice(0, 3).map((pref, index) => (
            <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
              {pref}
            </span>
          ))}
          {client.preferences.length > 3 && (
            <span className="text-xs text-gray-500">+{client.preferences.length - 3}</span>
          )}
        </div>
      </div>

      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Activité Récente</h4>
        {client.recentActivity.slice(0, 2).map((activity, index) => (
          <div key={index} className="text-xs text-gray-600 mb-1">
            <span className="font-medium">{activity.date}</span> {activity.type} {activity.description} €{(activity.amount / 1000).toFixed(0)}K
          </div>
        ))}
      </div>

      <div className="flex space-x-2">
        <button className="flex items-center space-x-1 flex-1 px-3 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50">
          <Phone size={14} />
          <span>Appeler</span>
        </button>
        <button className="flex items-center space-x-1 flex-1 px-3 py-2 text-green-600 border border-green-300 rounded hover:bg-green-50">
          <Mail size={14} />
          <span>Message</span>
        </button>
        <button
          onClick={() => {
            setSelectedClient(client.id);
            setShowClientModal(true);
          }}
          className="flex items-center space-x-1 flex-1 px-3 py-2 text-purple-600 border border-purple-300 rounded hover:bg-purple-50"
        >
          <Calendar size={14} />
          <span>Réserver</span>
        </button>
      </div>
    </div>
  );

  const ClientDetailsModal = () => {
    if (!showClientModal || !selectedClient) return null;

    const client = vipClients.find(c => c.id === selectedClient);
    if (!client) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl ${getTierColor(client.tier)}`}>
                {getTierIcon('black')}
              </div>
              <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                {client.initials}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{client.name}</h2>
                <p className="text-gray-600">{client.title} • Member Since {client.since}</p>
                <div className="flex items-center space-x-4 mt-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTierColor(client.tier)}`}>
                    👑 Black Tier
                  </span>
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                    🌟 VIP Since {client.vipSince}
                  </span>
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                    🏆 Top 1% Client
                  </span>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                    ♻️ {client.bookings} Bookings
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowClientModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar gauche */}
              <div className="space-y-6">
                {/* Informations Contact */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Informations Contact</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Téléphone</strong><br/>{clientDetails.contact.phone}</div>
                    <div><strong>Email</strong><br/>{clientDetails.contact.email}</div>
                    <div><strong>Assistant</strong><br/>{clientDetails.contact.assistant}</div>
                    <div><strong>Adresse</strong><br/>{clientDetails.contact.address}</div>
                  </div>
                </div>

                {/* Membres Famille */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Membres Famille</h3>
                  <div className="space-y-3">
                    {clientDetails.family.map((member, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {member.initials}
                        </div>
                        <div>
                          <div className="font-medium text-gray-800">{member.name}</div>
                          <div className="text-xs text-gray-600">{member.role}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Résumé Financier */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3">Résumé Financier</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total Lifetime</span>
                      <span className="font-bold">€{(clientDetails.financial.totalLifetime / 1000000).toFixed(1)}M</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cette Année</span>
                      <span className="font-bold">€{(clientDetails.financial.thisYear / 1000000).toFixed(2)}M</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Panier Moyen</span>
                      <span className="font-bold">€{(clientDetails.financial.averageBooking / 1000).toFixed(0)}K</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Commission Totale</span>
                      <span className="font-bold text-green-600">€{(clientDetails.financial.totalCommission / 1000).toFixed(0)}K</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div>
                      <div className="font-bold text-gray-800">{clientDetails.financial.totalStays}</div>
                      <div className="text-xs text-gray-600">Séjours Total</div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-800">{clientDetails.financial.averageRating}</div>
                      <div className="text-xs text-gray-600">Note Moyenne</div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-800">{clientDetails.financial.satisfactionRate}%</div>
                      <div className="text-xs text-gray-600">Taux Satisfaction</div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-3">
                    <div className="font-bold text-gray-800">{clientDetails.financial.bookings2025}</div>
                    <div className="text-xs text-gray-600">Réservations 2025</div>
                  </div>
                </div>
              </div>

              {/* Contenu principal */}
              <div className="lg:col-span-3 space-y-6">
                {/* Préférences Détaillées */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Préférences Détaillées</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>🏝️</span>
                          <strong>Destinations</strong>
                        </div>
                        <p className="text-sm text-gray-700">{clientDetails.preferences.destinations}</p>
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>🍽️</span>
                          <strong>Cuisine</strong>
                        </div>
                        <p className="text-sm text-gray-700">{clientDetails.preferences.cuisine}</p>
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>⚠️</span>
                          <strong>Allergies</strong>
                        </div>
                        <p className="text-sm text-red-700">{clientDetails.preferences.allergies}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>🍷</span>
                          <strong>Vins</strong>
                        </div>
                        <p className="text-sm text-gray-700">{clientDetails.preferences.wines}</p>
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>🛏️</span>
                          <strong>Literie</strong>
                        </div>
                        <p className="text-sm text-gray-700">{clientDetails.preferences.bedding}</p>
                      </div>
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span>🎯</span>
                          <strong>Activités</strong>
                        </div>
                        <p className="text-sm text-gray-700">{clientDetails.preferences.activities}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Historique Récent */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Historique Récent</h3>
                  
                  <div className="space-y-4">
                    {clientDetails.history.map((booking, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-800">{booking.property}</h4>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            booking.status === 'Confirmé' ? 'bg-green-100 text-green-800' : 
                            booking.status === 'Complété' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>{booking.dates} • {booking.duration} • {booking.guests}</div>
                          <div className="font-medium text-green-600">€{booking.amount.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes Internes */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-800 mb-4">Notes Internes</h3>
                  
                  <div className="space-y-4">
                    {clientDetails.notes.map((note, index) => (
                      <div key={index} className="border-l-4 border-yellow-400 pl-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <strong>{note.author}</strong>
                            <span className="text-gray-500 text-sm ml-2">{note.date}</span>
                          </div>
                          <div className="flex space-x-1">
                            {note.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredClients = vipClients.filter(client => {
    const matchesTier = selectedTier === 'all' || client.tier === selectedTier;
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTier && matchesSearch;
  });

  return (
    <div className="vip-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">VIP Management</h1>
            <p className="text-gray-600 mt-1">Gestion de votre clientèle UHNW et ultra-premium</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <User size={18} />
              <span>Profil Client</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <BarChart3 size={18} />
              <span>Analytics</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau VIP</span>
            </button>
          </div>
        </div>

        {/* Tiers VIP */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {vipTiers.map((tier) => (
            <div key={tier.id} className="bg-white border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">{tier.icon}</div>
              <div className="font-semibold text-gray-800">{tier.name}</div>
              <div className="text-2xl font-bold text-gray-800 my-2">{tier.count}</div>
              <div className="text-xs text-gray-600">{tier.range}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6">
        {/* Filtres */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous les tiers</option>
              <option value="black">Black (€1M+)</option>
              <option value="diamond">Diamond (€500K-1M)</option>
              <option value="platinum">Platinum (€200K-500K)</option>
              <option value="gold">Gold (€50K-200K)</option>
            </select>

            <select
              value={selectedOrigin}
              onChange={(e) => setSelectedOrigin(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes</option>
              <option value="us">États-Unis</option>
              <option value="europe">Europe</option>
              <option value="middle-east">Moyen-Orient</option>
              <option value="asia">Asie</option>
              <option value="latin-america">Amérique Latine</option>
            </select>

            <select
              value={selectedActivity}
              onChange={(e) => setSelectedActivity(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">Cette année</option>
            </select>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous</option>
              <option value="family">Famille</option>
              <option value="couple">Couple</option>
              <option value="corporate">Corporate</option>
              <option value="celebrity">Célébrité</option>
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Recherche"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Cartes clients VIP */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredClients.map((client) => (
            <VipClientCard key={client.id} client={client} />
          ))}
        </div>
      </div>

      <ClientDetailsModal />
    </div>
  );
};

export default VipManagement;